import { useState } from 'react';
import { Image as ImageIcon, CheckCircle, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { Card, Button, Spinner, Alert } from 'react-bootstrap';
import { SectionHeader } from '../shared/SectionHeader';
import { IdBadge, IdBadgeType } from '../../../../../../components/ui/badge/IdBadge';
import { mediaClient } from '../../../../../../infrastructure/MediaClient';
import type { SkinSeoulAdminProduct } from '../../../../../../models/ProductTypes';
import { SkinSeoulProductType } from '../../../../../../models/ProductTypes';

interface PublishedImagePanelThuProps {
  readonly productsBySku: SkinSeoulAdminProduct[];
  readonly imageRefreshKey: number;
  readonly recentlyPublishedSkus: Set<string>;
  readonly publishingSkus: Set<string>;
  readonly onRefreshImages: () => void;
}

export const PublishedImagePanelThu = ({
  productsBySku,
  imageRefreshKey,
  recentlyPublishedSkus,
  publishingSkus,
  onRefreshImages,
}: PublishedImagePanelThuProps) => {
  const [deletingImages, setDeletingImages] = useState<Set<string>>(new Set());
  const [deleteMessage, setDeleteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleDeleteGalleryImage = async (imageUrl: string) => {
    const productsWithThisImage = productsBySku.filter(
      (product) => product.productImageUrls && product.productImageUrls.includes(imageUrl),
    );

    if (productsWithThisImage.length === 0) {
      setDeleteMessage({ type: 'error', text: 'No products found with this image' });
      return;
    }

    setDeletingImages((prev) => new Set(prev).add(imageUrl));
    setDeleteMessage(null);

    try {
      let successCount = 0;
      let failedCount = 0;

      for (const product of productsWithThisImage) {
        if (!product.sku) {
          continue;
        }

        const response = await mediaClient.publishGalleryImages({
          skus: [product.sku],
          toDelete: [imageUrl],
        });

        const result = response.results[0];
        if (result?.success) {
          successCount++;
        } else {
          failedCount++;
          console.error(`Failed to delete from SKU ${product.sku}:`, result?.message);
        }
      }

      if (failedCount === 0) {
        setDeleteMessage({
          type: 'success',
          text: `Gallery image deleted from ${successCount} SKU(s) successfully!`,
        });
      } else {
        setDeleteMessage({
          type: 'error',
          text: `Partial success: ${successCount} succeeded, ${failedCount} failed`,
        });
      }

      setTimeout(() => {
        onRefreshImages();
        setDeleteMessage(null);
      }, 1500);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      setDeleteMessage({ type: 'error', text: `Error: ${errorMsg}` });
    } finally {
      setDeletingImages((prev) => {
        const updated = new Set(prev);
        updated.delete(imageUrl);
        return updated;
      });
    }
  };

  return (
    <div className="mb-4">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <SectionHeader title="Published Thumbnails in WP" />
        <Button
          variant="outline-primary"
          size="sm"
          onClick={onRefreshImages}
          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px' }}
        >
          <RefreshCw size={14} />
          <span style={{ fontSize: '0.75rem' }}>Refresh</span>
        </Button>
      </div>

      {productsBySku.length === 0 ? (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            background: 'white',
            borderRadius: '8px',
            border: '2px dashed #dee2e6',
          }}
        >
          <ImageIcon size={48} className="mb-3 opacity-25" />
          <p className="mb-0 text-muted">No products published yet</p>
          <small className="text-muted">Publish images to see them here</small>
        </div>
      ) : (
        <>
          {/* Featured Thumbnails for each SKU */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
            {productsBySku
              .filter((p) => p.type === SkinSeoulProductType.Variable || p.type === SkinSeoulProductType.Simple)
              .map((product) => {
                const featuredImage = product.productImageUrls?.[0];
                const imageUrlWithKey = featuredImage ? `${featuredImage}?v=${imageRefreshKey}` : undefined;

                return (
                  <Card
                    key={`${product.sku}-${imageRefreshKey}`}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <Card.Body style={{ padding: '12px' }}>
                      <div className="mb-2">
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>
                          {product.name || 'Product'}
                        </div>
                        <IdBadge type={IdBadgeType.SKU} value={product.sku || 'No SKU'} size="sm" />
                      </div>

                      {featuredImage ? (
                        <div>
                          <div
                            style={{
                              position: 'relative',
                              background: '#f8f9fa',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              border: recentlyPublishedSkus.has(product.sku || '')
                                ? '2px solid #0d6efd'
                                : '2px solid #28a745',
                              marginBottom: '6px',
                            }}
                          >
                            <img
                              src={imageUrlWithKey}
                              alt={`Featured thumbnail for ${product.sku}`}
                              style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                              }}
                              key={imageRefreshKey}
                            />

                            {/* Publishing overlay */}
                            {publishingSkus.has(product.sku || '') && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: 'rgba(0, 0, 0, 0.6)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}
                                >
                                  <Loader2
                                    size={24}
                                    color="white"
                                    style={{
                                      animation: 'spin 1s linear infinite',
                                    }}
                                  />
                                  <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 500 }}>
                                    Publishing...
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Published overlay */}
                            {recentlyPublishedSkus.has(product.sku || '') && !publishingSkus.has(product.sku || '') && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: 'rgba(40, 167, 69, 0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <CheckCircle size={32} color="#28a745" strokeWidth={3} />
                              </div>
                            )}

                            {/* Featured badge */}
                            <div
                              style={{
                                position: 'absolute',
                                top: '4px',
                                left: '4px',
                                background: 'rgba(40, 167, 69, 0.95)',
                                borderRadius: '3px',
                                padding: '2px 6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                              }}
                            >
                              <CheckCircle size={10} color="white" />
                              <span style={{ color: 'white', fontSize: '0.6rem', fontWeight: 600 }}>Featured</span>
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: '0.7rem',
                              color: '#6c757d',
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                            }}
                          >
                            {featuredImage}
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            background: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px dashed #dee2e6',
                          }}
                        >
                          <small className="text-muted">No thumbnail published</small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                );
              })}
          </div>

          {/* Shared Gallery Images */}
          {(() => {
            // Use Set to automatically remove duplicate URLs
            const allGalleryImages = new Set<string>();
            // Track how many SKUs share each image
            const imageUsageCount = new Map<string, number>();

            productsBySku.forEach((product) => {
              if (product.productImageUrls && product.productImageUrls.length > 1) {
                product.productImageUrls.slice(1).forEach((url) => {
                  allGalleryImages.add(url);
                  imageUsageCount.set(url, (imageUsageCount.get(url) || 0) + 1);
                });
              }
            });

            if (allGalleryImages.size > 0) {
              return (
                <div>
                  <small className="text-muted d-block mb-2" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    Shared Gallery Images ({allGalleryImages.size} unique)
                  </small>

                  {deleteMessage && (
                    <Alert
                      variant={deleteMessage.type === 'success' ? 'success' : 'danger'}
                      dismissible
                      onClose={() => setDeleteMessage(null)}
                      style={{ fontSize: '0.85rem', padding: '8px 12px', marginBottom: '12px' }}
                    >
                      {deleteMessage.text}
                    </Alert>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    {Array.from(allGalleryImages).map((url, idx) => {
                      const isDeleting = deletingImages.has(url);
                      const galleryUrlWithKey = `${url}?v=${imageRefreshKey}`;
                      const usageCount = imageUsageCount.get(url) || 1;
                      return (
                        <div key={`gallery-${idx}-${imageRefreshKey}`}>
                          <div
                            style={{
                              position: 'relative',
                              paddingBottom: '100%',
                              background: '#f8f9fa',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              border: '1px solid #dee2e6',
                              marginBottom: '4px',
                              opacity: isDeleting ? 0.5 : 1,
                            }}
                          >
                            <img
                              src={galleryUrlWithKey}
                              alt={`Gallery ${idx + 1}`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                              loading="lazy"
                              key={imageRefreshKey}
                            />

                            {/* Shared badge - shows how many SKUs use this image */}
                            {usageCount > 1 && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  left: '4px',
                                  background: 'rgba(13, 110, 253, 0.95)',
                                  borderRadius: '3px',
                                  padding: '2px 6px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px',
                                }}
                              >
                                <CheckCircle size={10} color="white" />
                                <span style={{ color: 'white', fontSize: '0.6rem', fontWeight: 600 }}>
                                  Shared by {usageCount} SKUs
                                </span>
                              </div>
                            )}

                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDeleteGalleryImage(url)}
                              disabled={isDeleting}
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                padding: '4px 8px',
                                fontSize: '0.7rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              {isDeleting ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    style={{ width: '10px', height: '10px' }}
                                  />
                                  <span>Deleting</span>
                                </>
                              ) : (
                                <>
                                  <Trash2 size={10} />
                                  <span>Delete</span>
                                </>
                              )}
                            </Button>
                          </div>
                          <div
                            style={{
                              fontSize: '0.7rem',
                              color: '#6c757d',
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                            }}
                          >
                            {url}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </>
      )}
    </div>
  );
};
